/**
 * ================================================================================================
 *  THE SIDEBAR, AS THE APPROVED PLATFORM PROTOTYPE DRAWS IT.
 * ================================================================================================
 *
 * The kit's own markup, for reference:
 *
 *     nav.gp-nav > button[data-page][aria-current] > i[data-lucide] + label
 *
 * One flat list, one icon per entry, the current entry filled blue. That is the whole platform
 * navigation in the approved design — there is no favourites widget and no tab strip in it.
 *
 * ── A CORRECTION, AND WHY IT MATTERED ───────────────────────────────────────────────────────────
 *
 * The first implementation put favourites and a bounded tab strip into THIS sidebar. They are real
 * approved features, but they belong to the OTHER kit: `GLPOINT-CUSTOMER-DESIGN-2026-10-15.zip`
 * builds the customer workspace out of a different class family entirely — `gp-w-favorites`,
 * `gp-w-tabs`, `gp-w-library`, `gp-w-pin`, `gp-w-star` — none of which appears in the platform kit.
 * Putting the customer workspace's furniture into the admin console produced a sidebar that
 * matched neither approved design.
 *
 * So they moved to where their own kit draws them, and this component is the rail on both
 * surfaces and nothing else: the work tabs are `WorkTabs`, across the top of the content, and the
 * favourites are a panel ON the customer home, where there is room to say what each one is and
 * what state it is in.
 *
 * ── NAVIGATION IS AN ANCHOR, NOT A ROUTER CALL ──────────────────────────────────────────────────
 *
 * `WorkContext` deliberately exposes no `navigate`: selecting rewrites the ADDRESS and the gate
 * re-decides on the next call (CM-014). `useNavigate()` would also couple this component to a
 * Router the screen harness does not mount, so every screen test would throw on a component none
 * of them is testing. The product already works this way — the harness header records that *"every
 * in-app navigation is a full page load"*.
 *
 * A planned destination gets no anchor at all: it is a `span`, so it cannot be clicked into a route
 * that does not exist, and it carries its own state and its own SCOPE in the menu.
 *
 */

import { useMemo } from 'react';
import { NavTasks } from './NavTasks';

import { useLocale } from '../i18n/LocaleProvider';
import { Icon } from './Icon';
import {
  visibleDestinations,
  visibleMembers,
  type Destination,
  type Surface,
} from './destinations';

export interface ShellNavProps {
  readonly surface: Surface;
  readonly capabilities: readonly string[];
  /** The address now, so the active entry is read from the address rather than remembered. */
  readonly currentPath: string;
}

export function ShellNav({ surface, capabilities, currentPath }: ShellNavProps): JSX.Element {
  const { t } = useLocale();
  const destinations = useMemo(
    () => visibleDestinations(surface, capabilities),
    [surface, capabilities],
  );





  const entries = destinations.map((d) => {
    /*
     * ── A GROUP: one approved destination, more than one screen behind it ──────────────────────
     *
     * The heading is NOT a link, and does not pretend to be: it opens nothing, so it is rendered
     * as a heading and never as a disabled control. Its members are gated one by one on the
     * capability each member's own route requires, so a session holding one of the two sees one
     * link — and the gate decides again on the request regardless of what was drawn here.
     */
    if (d.members !== undefined) {
      const members = visibleMembers(d, capabilities);
      const here = members.some((m) => m.href === currentPath);
      /*
       * A DISCLOSURE, not a permanent list. `details` because it is one: a native disclosure is
       * keyboard-operable, announced as one, and needs no state of its own. It opens when the
       * address is already inside it, so arriving at a member never leaves the person looking at a
       * closed group that does not explain where they are.
       */
      return (
        <details
          className="gp-nav-group"
          key={d.id}
          {...(d.collapsed === true && !here ? {} : { open: true })}
        >
          <summary className="gp-nav-group-title">
            <span className="gp-disclosure" aria-hidden="true">
              ❯
            </span>
            <Icon name={d.icon} />
            <span className="gp-nav-label">{t(d.label)}</span>
            <span className="gp-nav-note" dir="ltr">
              {members.length}
            </span>
          </summary>
          {members.map((m) =>
            m.href === undefined ? (
              <span
                key={m.id}
                className="gp-nav-planned gp-nav-member"
                {...(m.plannedScope === undefined ? {} : { title: t(m.plannedScope) })}
              >
                <span className="gp-nav-label">
                  {t(m.label)}
                  <span className="gp-nav-note">{t('state.notImplemented')}</span>
                </span>
              </span>
            ) : (
              <a
                key={m.id}
                className="gp-nav-member"
                href={m.href}
                {...(m.href === currentPath ? { 'aria-current': 'page' as const } : {})}
              >
                {t(m.label)}
              </a>
            ),
          )}
        </details>
      );
    }

    const active = d.href !== undefined && d.href === currentPath;
    if (d.href === undefined) {
      return (
        <span
          key={d.id}
          className="gp-nav-planned"
          {...(d.plannedScope === undefined ? {} : { title: t(d.plannedScope) })}
        >
          <Icon name={d.icon} />
          <span className="gp-nav-label">
            {t(d.label)}
            <span className="gp-nav-note">{t('state.notImplemented')}</span>
          </span>
        </span>
      );
    }
    return (
      <a key={d.id} href={d.href} {...(active ? { 'aria-current': 'page' as const } : {})}>
        <Icon name={d.icon} />
        <span className="gp-nav-label">{t(d.label)}</span>
      </a>
    );
  });

  if (surface === 'PLATFORM') {
    // The kit's platform sidebar: the list, and nothing beside it.
    return (
      <nav className="gp-nav" aria-label={t('shell.platformConsole')}>
        {entries}
      </nav>
    );
  }

  /*
   * THE CUSTOMER WORKSPACE RAIL, AND `בשימוש לאחרונה` UNDER IT. **§3, 2026-10-25.**
   *
   * ── THE CORRECTION, AND IT REVERSES PART OF THE ONE ABOVE ────────────────────────────────────
   *
   * The header of this file records moving a tab strip and a favourites widget OUT of this rail,
   * because the customer kit draws neither here. The strip went to the top of the content — and
   * that is where the Owner saw it and rejected it, with the alternative stated:
   *
   *     *"זה לא צריך להיות בטאבים, זה יכול להיות מרוכז בצד שמאל תחת כותרת בשימוש לאחרונה כמו שיש
   *     בפוריטי"*
   *
   * So the list comes back to the rail, and the earlier objection is answered rather than ignored:
   * what competed with the navigation was a row of TABS with the same silhouette as the primary
   * action. A quiet list under a stated heading, below the destinations, does not — which is what
   * the Priority screen he attached does with its own recent block.
   *
   * The favourites stay on the home screen. They were moved for a different reason (there is room
   * there to say what each one is and what state it is in) and that reason still holds.
   *
   * It is rendered only on the customer surface, because it is the customer workspace's furniture —
   * the platform kit has no such block and the console does not grow one.
   */
  return (
    <nav className="gp-nav" aria-label={t('shell.myWorkspace')}>
      {entries}
      {/*
        ── משימות, UNDER הגדרות. **Owner, 2026-11-01.** ─────────────────────────────────────────

        *"ביקשתי שזה יהיה בוויג׳ט בצד ימין מתחת להגדרות."* `הגדרות` is the last entry in
        `entries` above, so "under it" is here. It renders nothing without `tasks.item.read` and
        issues no request without it either — see the component, which also states the cost of
        living in the shell rather than on one screen.
      */}
      <NavTasks capabilities={capabilities} />
    </nav>
  );
}
